import CardGiftcardRoundedIcon from '@mui/icons-material/CardGiftcardRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import { Box, keyframes, Stack, styled } from '@mui/material';
import type { Control } from 'react-hook-form';
import PricingDetails from 'src/pages/Store/components/common/PricingDetails';
import type { GetCartResponse } from 'src/openapi';
import {
  introDealOf,
  proRecurringTerms,
  shownFreeCycles,
} from 'src/helpers/subscription';
import { SectionEyebrow, SectionTitle } from 'src/pages/Store/styles';
import {
  CheckoutAddress,
  CheckoutDetails,
  StoreInitialState,
} from 'src/redux/types/store';
import BillingAddressCard from './BillingAddressCard';
import BillingAddressForm from './BillingAddressForm';

// Rolls down over what it replaces, not above it — a Collapse in the flow
// pushed the totals down instead. It never scrolls: a scrollbar inside a panel
// inside a dialog is two nested scrollers over one short form, so where there
// is width the form goes two columns instead.
const rollDown = keyframes`
  from { opacity: 0; transform: translateY(-10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const AddressShutter = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  minHeight: '100%',
  zIndex: 3,
  padding: '14px',
  borderRadius: '14px',
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: '0 18px 40px -22px rgba(16, 24, 40, 0.55)',
  animation: `${rollDown} 220ms ease-out`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
}));

// What the buyer is paying for on one side, what it costs on the other, so the
// step fits its sheet at the same height as the screen it came from.
export const CheckoutSplit = styled(Box)(({ theme }) => ({
  position: 'relative',
  // Fills the sheet's fixed height so the two columns sit centred in it rather
  // than pinned to the top with the slack below them.
  flex: 1,
  minHeight: 0,
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 18,
  alignItems: 'start',
  alignContent: 'center',
  [theme.breakpoints.down('md')]: { gridTemplateColumns: '1fr' },
}));

// The summary arrives after the plan card has taken its place beside it — it
// fades in rather than appearing, so the two read as one movement.
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const FadeIn = styled(Box)({
  animation: `${fadeIn} 380ms ease-out both`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
});

/** The free days a cart's Pro mandate starts with, where the buyer's intro
 * offer gives days in place of the free month; null for months. */
const shownFreeDays = (cart?: GetCartResponse | null) =>
  proRecurringTerms(
    cart?.cart?.find(
      (line) =>
        line.item?.is_recurring &&
        (line.item.subscription_free_cycles ?? 0) > 0,
    )?.item,
  ).freeDays;

/** A Pro mandate with a free first period says so; any other cart is a total. */
export const OrderSummaryHeading = ({
  cart,
}: {
  cart?: GetCartResponse | null;
}) => {
  const freeMonth = shownFreeCycles(cart) > 0;
  const freeDays = shownFreeDays(cart);
  const deal = introDealOf(
    cart?.cart?.find((line) => line.item?.is_recurring)?.item,
  );
  return (
    <Stack sx={{ gap: '4px' }}>
      <SectionEyebrow>
        {freeMonth || deal ? (
          <CardGiftcardRoundedIcon sx={{ fontSize: 13 }} />
        ) : (
          <ReceiptLongRoundedIcon sx={{ fontSize: 13 }} />
        )}
        {deal
          ? 'Your first-month deal'
          : !freeMonth
            ? 'Order summary'
            : freeDays
              ? `Your ${freeDays} free days of Pro`
              : 'Your free month of Pro'}
      </SectionEyebrow>
      <SectionTitle sx={{ fontSize: '24px' }}>
        {deal
          ? `Your first month for ₹${deal.price}`
          : !freeMonth
            ? 'Cart Total'
            : freeDays
              ? `Start your ${freeDays}-day free trial`
              : 'Start your free month'}
      </SectionTitle>
    </Stack>
  );
};

const SummaryStep = ({
  store,
  name,
  billingAddress,
  isGemsOnly,
  disabled,
  userGems,
  onChangeAddress,
  onContinue,
  addressOpen,
  addressControl,
  onAddressSubmit,
}: {
  store: StoreInitialState;
  name: string;
  billingAddress: CheckoutAddress;
  isGemsOnly: boolean;
  disabled: boolean;
  userGems?: number;
  onChangeAddress: () => void;
  onContinue: (useGems: boolean) => void;
  /** Editing happens in place, over the cart — never as a second popup. */
  addressOpen: boolean;
  addressControl: Control<CheckoutDetails>;
  onAddressSubmit: () => void;
}) => {
  // A free first period leads with the offer; the address it is billed to is
  // bookkeeping, so it comes after.
  const freeMonth = shownFreeCycles(store.cartDetails) > 0;
  const address = (
    <BillingAddressCard
      name={name}
      billingAddress={billingAddress}
      onChange={onChangeAddress}
      open={addressOpen}
    />
  );
  return (
    <Stack sx={{ gap: '12px' }}>
      {/* The heading belongs at the top of the sheet: under the address card it
          reads as a label for the address rather than for the purchase. */}
      <OrderSummaryHeading cart={store.cartDetails} />
      {!freeMonth && address}
      <Box sx={{ position: 'relative' }}>
        <PricingDetails
          store={store}
          variant="checkout"
          isGemsOnly={isGemsOnly}
          disabled={disabled}
          onContinue={onContinue}
          userGems={userGems}
          fullWidth
          hideHeader
        />
        {/* Mounted only while open, so a half-edited address unregisters its
            fields on close rather than blocking payment from behind a shutter
            the buyer can no longer see. */}
        {addressOpen && (
          <AddressShutter>
            <BillingAddressForm
              control={addressControl}
              onClose={onChangeAddress}
              onSubmit={onAddressSubmit}
            />
          </AddressShutter>
        )}
      </Box>
      {freeMonth && address}
    </Stack>
  );
};

export default SummaryStep;
